import type { ReactNode } from 'react';

// Iconos de los módulos: trazo de 1,6 sobre una cuadrícula de 20 × 20, para que convivan con IBM Plex.

function Icon({ children, className = 'h-5 w-5' }: { children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 20 20" className={`shrink-0 ${className}`} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}

export type IconName =
  | 'inicio'
  | 'tickets'
  | 'proyectos'
  | 'tareas'
  | 'inventario'
  | 'monitoreo'
  | 'usuarios'
  | 'conocimiento'
  | 'kpis'
  | 'parametros'
  | 'roles'
  | 'asignaciones'
  | 'organizaciones'
  | 'tenants'
  | 'reportar'
  | 'pedir'
  | 'colapsar'
  | 'expandir';

const PATHS: Record<IconName, ReactNode> = {
  // Panel del día: el ticket activo arriba y dos columnas debajo.
  inicio: (
    <>
      <rect x="3" y="3" width="14" height="5" rx="1.2" />
      <rect x="3" y="10.5" width="6" height="6.5" rx="1.2" />
      <rect x="11" y="10.5" width="6" height="6.5" rx="1.2" />
    </>
  ),
  // Talón de ticket con la muesca y la línea de corte.
  tickets: (
    <>
      <path d="M3 6.5V5a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v1.5a2 2 0 0 0 0 4V15a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4.5a2 2 0 0 0 0-4Z" />
      <path d="M12.5 4.5v1.5M12.5 9v2M12.5 14v1.5" />
    </>
  ),
  // Barras escalonadas de un Gantt.
  proyectos: (
    <>
      <path d="M3 3.5v13" />
      <rect x="5" y="4.5" width="7" height="2.5" rx="1" />
      <rect x="8" y="8.75" width="8" height="2.5" rx="1" />
      <rect x="6.5" y="13" width="5" height="2.5" rx="1" />
    </>
  ),
  tareas: (
    <>
      <path d="m3.5 5.5 1.5 1.5 2.5-3" />
      <path d="M10 5.5h6.5" />
      <path d="m3.5 13 1.5 1.5 2.5-3" />
      <path d="M10 13h6.5" />
    </>
  ),
  // Portátil.
  inventario: (
    <>
      <rect x="4" y="4" width="12" height="8.5" rx="1" />
      <path d="M2 15.5h16" />
    </>
  ),
  // Pulso sobre una pantalla: lo que está vivo y cómo late.
  monitoreo: (
    <>
      <rect x="2.5" y="3.5" width="15" height="11" rx="1.2" />
      <path d="M5 9.5h2.25l1.25-2.5 2 5 1.25-2.5H15" />
      <path d="M7 17h6" />
    </>
  ),
  usuarios: (
    <>
      <circle cx="7.5" cy="7" r="2.75" />
      <path d="M2.5 16c.6-2.6 2.6-4 5-4s4.4 1.4 5 4" />
      <path d="M13 4.5a2.5 2.5 0 0 1 0 5M14.5 12.2c1.5.5 2.6 1.8 3 3.8" />
    </>
  ),
  conocimiento: (
    <>
      <path d="M10 5.5C8.5 4.3 6 4 3 4.5v11c3-.5 5.5-.2 7 1 1.5-1.2 4-1.5 7-1v-11c-3-.5-5.5-.2-7 1Z" />
      <path d="M10 5.5v11" />
    </>
  ),
  kpis: (
    <>
      <path d="M3 16.5h14" />
      <path d="M5.5 13.5v-3M9.5 13.5v-6M13.5 13.5v-4.5" />
      <path d="m4.5 7.5 4-3.5 3.5 2.5 4-3" />
    </>
  ),
  parametros: (
    <>
      <path d="M3 5.5h8M14.5 5.5H17M3 10h2.5M9 10h8M3 14.5h6M12.5 14.5H17" />
      <circle cx="12.75" cy="5.5" r="1.75" />
      <circle cx="7.25" cy="10" r="1.75" />
      <circle cx="10.75" cy="14.5" r="1.75" />
    </>
  ),
  // Escudo con un visto: qué puede hacer cada rol.
  roles: (
    <>
      <path d="M10 2.75 16 5v4.5c0 3.6-2.5 6.3-6 7.75-3.5-1.45-6-4.15-6-7.75V5l6-2.25Z" />
      <path d="m7.5 10 1.75 1.75L12.75 8" />
    </>
  ),
  // Persona con una credencial: quién tiene qué rol.
  asignaciones: (
    <>
      <circle cx="7.5" cy="6.75" r="2.75" />
      <path d="M2.5 16c.6-2.6 2.6-4 5-4 1 0 1.9.2 2.7.7" />
      <rect x="12" y="11" width="5.5" height="6" rx="1" />
      <path d="M13.75 13.5h2" />
    </>
  ),
  organizaciones: (
    <>
      <path d="M3.5 17V5.5a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1V17" />
      <path d="M11.5 9h4a1 1 0 0 1 1 1v7" />
      <path d="M2.5 17h15" />
      <path d="M6 7.5h3M6 10.5h3M6 13.5h3" />
    </>
  ),
  tenants: (
    <>
      <circle cx="10" cy="10" r="7" />
      <path d="M3 10h14M10 3c2 2 2.75 4.4 2.75 7S12 15 10 17c-2-2-2.75-4.4-2.75-7S8 5 10 3Z" />
    </>
  ),
  // Algo que dejó de funcionar: aviso.
  reportar: (
    <>
      <path d="M8.7 3.6 2.6 14.2A1.5 1.5 0 0 0 3.9 16.5h12.2a1.5 1.5 0 0 0 1.3-2.3L11.3 3.6a1.5 1.5 0 0 0-2.6 0Z" />
      <path d="M10 8v3.5M10 14v.01" />
    </>
  ),
  // Un pedido: una caja.
  pedir: (
    <>
      <path d="m3 6.5 7-3.5 7 3.5v7L10 17l-7-3.5v-7Z" />
      <path d="m3 6.5 7 3.5 7-3.5M10 10v7" />
    </>
  ),
  colapsar: (
    <>
      <rect x="2.5" y="3.5" width="15" height="13" rx="1.5" />
      <path d="M7.5 3.5v13" />
      <path d="m13 8-2 2 2 2" />
    </>
  ),
  expandir: (
    <>
      <rect x="2.5" y="3.5" width="15" height="13" rx="1.5" />
      <path d="M7.5 3.5v13" />
      <path d="m11 8 2 2-2 2" />
    </>
  ),
};

export function ModuleIcon({ name, className }: { name: IconName; className?: string }) {
  return <Icon className={className}>{PATHS[name]}</Icon>;
}
